/**
 * UI state (TODO-005). DOM-free so it can be unit-tested.
 *
 * Inputs are stored as the raw strings the rep typed, so re-renders never
 * fight the keyboard. `toBidInput` parses them into the engine contract.
 *
 * V1 = Octopus plan + FREE Octopus battery, NO SOLAR (DESIGN IMPORTANT
 * callout). The UI never sends `solar` to the engine.
 */
import {
  TDUS,
  TERM_OPTIONS,
  regionsForTdu,
  isValidRegionForTdu,
  type LoadArchetype,
  type RegionId,
  type TduId,
  type TermMonths,
} from "../config";
import { defaultStartMonth, type BidInput, type RateOverrides } from "../engine/bid";

export type Step = 1 | 2 | 3;
export type UsageMode = "monthly" | "annual" | "averageMonthly";
export type PlanMode = "itemized" | "allIn";

/** Battery sizes the rep can pick (kWh). Default 13.5. */
export const BATTERY_SIZES: readonly number[] = [10, 13.5, 15, 20];
export const DEFAULT_BATTERY_KWH = 13.5;

/** Customer session (cleared by "New customer"). */
export interface AppState {
  step: Step;
  tdu: TduId;
  region: RegionId;
  usageMode: UsageMode;
  /** Jan–Dec, raw strings. */
  monthlyKwh: string[];
  annualKwh: string;
  avgKwh: string;
  planMode: PlanMode;
  /** ¢/kWh */
  energyCents: string;
  /** $/month */
  baseFee: string;
  /** ¢/kWh incl. TDU */
  allInCents: string;
  /** $/month not included in the all-in rate */
  allInFee: string;
  startMonth: number;
  term: TermMonths;
  battery: { enabled: boolean; capacityKwh: number };
  ev: { enabled: boolean; mode: "new" | "existing"; miles: string; rateCents: string };
  nest: { enabled: boolean; pct: string };
}

/** Per-TDU rep overrides. Empty string = use config. */
export interface TduOverride {
  /** "default" | "alt:<index>" | "custom" */
  volChoice: string;
  volCents: string;
  customer: string;
  meter: string;
}

/** Rep settings from the ⚙️ drawer. Survive "New customer". */
export interface RepSettings {
  octRateCents: Record<TermMonths, string>;
  octBaseFee: string;
  tdu: Record<TduId, TduOverride>;
  vppRate: string;
  archetype: LoadArchetype;
}

export function defaultState(today: Date = new Date()): AppState {
  return {
    step: 1,
    tdu: "oncor",
    region: TDUS.oncor.defaultRegion,
    usageMode: "averageMonthly",
    monthlyKwh: new Array<string>(12).fill(""),
    annualKwh: "",
    avgKwh: "",
    planMode: "itemized",
    energyCents: "",
    baseFee: "",
    allInCents: "",
    allInFee: "",
    startMonth: defaultStartMonth(today),
    term: 12,
    battery: { enabled: true, capacityKwh: DEFAULT_BATTERY_KWH },
    ev: { enabled: false, mode: "new", miles: "12000", rateCents: "" },
    nest: { enabled: false, pct: "10" },
  };
}

const emptyTdu = (): TduOverride => ({ volChoice: "default", volCents: "", customer: "", meter: "" });

export function defaultRepSettings(): RepSettings {
  return {
    octRateCents: { 12: "", 18: "", 24: "" },
    octBaseFee: "",
    tdu: { oncor: emptyTdu(), centerpoint: emptyTdu(), tnmp: emptyTdu() },
    vppRate: "",
    archetype: "electric_heat",
  };
}

/** Parses a rep-typed number ("1,200", "$9.95", "14.5¢"). Blank/invalid → undefined. */
export function parseNum(raw: string | undefined): number | undefined {
  if (raw === undefined) return undefined;
  const s = raw.replace(/[,$¢%\s]/g, "");
  if (s === "") return undefined;
  const n = Number(s);
  return Number.isFinite(n) && n >= 0 ? n : undefined;
}

/** Changes TDU and keeps the region valid. */
export function setTdu(s: AppState, tdu: TduId): void {
  s.tdu = tdu;
  if (!isValidRegionForTdu(tdu, s.region)) s.region = TDUS[tdu].defaultRegion;
}

export { regionsForTdu };

export type BuildInputResult =
  | { ok: true; input: BidInput }
  | { ok: false; missing: string[] };

/** Maps UI state + rep settings to the engine contract. Never includes solar. */
export function toBidInput(s: AppState, rep: RepSettings): BuildInputResult {
  const missing: string[] = [];
  let usage: BidInput["usage"] | undefined;
  if (s.usageMode === "monthly") {
    const vals = s.monthlyKwh.map(parseNum);
    if (vals.some((v) => v === undefined)) missing.push("all 12 months of usage");
    else usage = { mode: "monthly", monthlyKwh: vals as number[] };
  } else if (s.usageMode === "annual") {
    const v = parseNum(s.annualKwh);
    if (v === undefined || v === 0) missing.push("annual usage");
    else usage = { mode: "annual", annualKwh: v };
  } else {
    const v = parseNum(s.avgKwh);
    if (v === undefined || v === 0) missing.push("average monthly usage");
    else usage = { mode: "averageMonthly", averageMonthlyKwh: v };
  }
  if (usage?.mode === "monthly" && usage.monthlyKwh.every((v) => v === 0)) {
    missing.push("usage above 0 kWh");
    usage = undefined;
  }

  let currentPlan: BidInput["currentPlan"] | undefined;
  if (s.planMode === "itemized") {
    const e = parseNum(s.energyCents);
    const f = parseNum(s.baseFee) ?? 0;
    if (e === undefined) missing.push("current energy rate");
    else currentPlan = { mode: "itemized", energyRate: e / 100, baseFee: f };
  } else {
    const r = parseNum(s.allInCents);
    const f = parseNum(s.allInFee) ?? 0;
    if (r === undefined) missing.push("current all-in rate");
    else currentPlan = { mode: "allIn", rate: r / 100, baseFee: f };
  }

  if (!usage || !currentPlan) return { ok: false, missing };

  const input: BidInput = {
    tdu: s.tdu,
    region: s.region,
    archetype: rep.archetype,
    usage,
    currentPlan,
    startMonth: s.startMonth,
    selectedTerm: s.term,
    battery: { enabled: s.battery.enabled, capacityKwh: s.battery.capacityKwh },
    ev: buildEv(s),
    nest: buildNest(s),
    overrides: toOverrides(s.tdu, rep),
  };
  return { ok: true, input };
}

function buildEv(s: AppState): NonNullable<BidInput["ev"]> {
  if (!s.ev.enabled) return { enabled: false, mode: s.ev.mode };
  const miles = parseNum(s.ev.miles);
  const rate = parseNum(s.ev.rateCents);
  return {
    enabled: true,
    mode: s.ev.mode,
    ...(miles !== undefined ? { annualMiles: miles } : {}),
    ...(rate !== undefined ? { evRate: rate / 100 } : {}),
  };
}

function buildNest(s: AppState): NonNullable<BidInput["nest"]> {
  if (!s.nest.enabled) return { enabled: false };
  const pct = parseNum(s.nest.pct);
  return { enabled: true, ...(pct !== undefined ? { coolingReductionPct: Math.min(pct, 100) / 100 } : {}) };
}

/** Rep drawer settings → engine overrides for the active TDU. */
export function toOverrides(tdu: TduId, rep: RepSettings): RateOverrides {
  const o: { -readonly [K in keyof RateOverrides]: RateOverrides[K] } = {};
  const rates: Partial<Record<TermMonths, number>> = {};
  for (const t of TERM_OPTIONS) {
    const v = parseNum(rep.octRateCents[t]);
    if (v !== undefined) rates[t] = v / 100;
  }
  if (Object.keys(rates).length) o.octopusEnergyRate = rates;
  const base = parseNum(rep.octBaseFee);
  if (base !== undefined) o.octopusBaseFee = base;
  const vpp = parseNum(rep.vppRate);
  if (vpp !== undefined) o.vppUsdPerKwhMonth = vpp;

  const t = rep.tdu[tdu];
  const cfg = TDUS[tdu];
  if (t.volChoice.startsWith("alt:")) {
    const alt = cfg.volumetricRate.alternates?.[Number(t.volChoice.slice(4))];
    if (alt) o.tduVolumetricRate = alt.value;
  } else if (t.volChoice === "custom") {
    const v = parseNum(t.volCents);
    if (v !== undefined) o.tduVolumetricRate = v / 100;
  }
  const cust = parseNum(t.customer);
  if (cust !== undefined) o.tduCustomerCharge = cust;
  const meter = parseNum(t.meter);
  if (meter !== undefined) o.tduMeteringCharge = meter;
  return o;
}

// ── Persistence ───────────────────────────────────────────────────────────
export const STATE_KEY = "octopus-tx-bid:session:v1";
export const REP_KEY = "octopus-tx-bid:rep:v1";

interface KV {
  getItem(k: string): string | null;
  setItem(k: string, v: string): void;
  removeItem(k: string): void;
}

/** Loads a saved session, merging onto defaults so new fields never break old saves. */
export function loadState(store: KV | undefined, today: Date = new Date()): AppState {
  const d = defaultState(today);
  try {
    const raw = store?.getItem(STATE_KEY);
    if (!raw) return d;
    const p = JSON.parse(raw) as Partial<AppState>;
    const s: AppState = {
      ...d,
      ...p,
      battery: { ...d.battery, ...p.battery },
      ev: { ...d.ev, ...p.ev },
      nest: { ...d.nest, ...p.nest },
      monthlyKwh: Array.isArray(p.monthlyKwh) && p.monthlyKwh.length === 12 ? p.monthlyKwh.map(String) : d.monthlyKwh,
    };
    if (!(s.tdu in TDUS)) s.tdu = d.tdu;
    if (!isValidRegionForTdu(s.tdu, s.region)) s.region = TDUS[s.tdu].defaultRegion;
    if (!TERM_OPTIONS.includes(s.term)) s.term = 12;
    if (![1, 2, 3].includes(s.step)) s.step = 1;
    return s;
  } catch {
    return d;
  }
}

export function loadRep(store: KV | undefined): RepSettings {
  const d = defaultRepSettings();
  try {
    const raw = store?.getItem(REP_KEY);
    if (!raw) return d;
    const p = JSON.parse(raw) as Partial<RepSettings>;
    return {
      ...d,
      ...p,
      octRateCents: { ...d.octRateCents, ...p.octRateCents },
      tdu: {
        oncor: { ...d.tdu.oncor, ...p.tdu?.oncor },
        centerpoint: { ...d.tdu.centerpoint, ...p.tdu?.centerpoint },
        tnmp: { ...d.tdu.tnmp, ...p.tdu?.tnmp },
      },
    };
  } catch {
    return d;
  }
}

export function save(store: KV | undefined, s: AppState, rep: RepSettings): void {
  try {
    store?.setItem(STATE_KEY, JSON.stringify(s));
    store?.setItem(REP_KEY, JSON.stringify(rep));
  } catch {
    /* storage full / private mode: autosave is best-effort */
  }
}

export function clearSession(store: KV | undefined): void {
  try {
    store?.removeItem(STATE_KEY);
  } catch {
    /* ignore */
  }
}
